import {ButtonType, ButtonWidget, DisplayValueFormatterProps, TabStyles} from '@tableau/widgets';
import {TypeUnsafeTranslation} from '~common/react/IReactTranslateService';
import {IContentRevision} from '~src/revisionHistory/serverApi/types/IContentRevision';
import * as React from 'react';
import {useRef} from 'react';
import {Icons} from '~common/react/Icons';
import {GenericButtonControlledFlyoutListMenu} from '~common/react/components/GenericButtonControlledFlyoutListMenu';
import * as _ from 'underscore';
import {
  RevisionHistoryDialogBodyState,
  RevisionHistoryDialogBodyStateAndProps,
} from '~src/revisionHistory/components/RevisionHistoryDialogBody';
import {DatasourceContentType, FlowContentType, IContentType, WorkbookContentType} from '~src/ts/models/ContentTypes';
import {Messages} from '~src/codegen/Localize';
import {BodyCell} from '@tableau/widgets-datagrid';
import {CommonStyles} from '~common/react/CommonStyles';
import {SiteUrls} from '@tableau/shared-urls';
import {getSessionManagementServiceInstance} from '~src/sessionManagement/SessionManagementService';
import {L10n} from '~src/ts/L10n/L10n';
import {RevisionActionWorkbook} from '~src/revisionHistoryWorkbook/services/RevisionActionWorkbook';
import {RevisionActionDatasource} from '~src/revisionHistoryDatasource/services/RevisionActionDatasource';
import {RevisionActionFlow} from '~src/revisionHistoryFlow/services/RevisionActionFlow';
import {RevisionHistoryActionButton, RevisionHistoryActionContext} from '~src/revisionHistory/interfaces/RevisionHistoryActionContext';

export const REVISION_ACTION_POP_UP_MENU_ITEM_ID='revision-action-popup-menu-item-id';
export const REVISION_ACTION_CELL_BUTTON_WIDGET_ID='revision-action-cell-button-widget-id';
export const REVISION_ACTION_CELL_FLYOUT_MENU_ID='revision-action-cell-generic-flyout-menu-id';

export const REVISION_NAME_SPAN_ID = 'revision-name-span-id';
export const REVISION_NAME_ANCHOR_ID = 'revision-name-anchor-id';
export const REVISION_NAME_DIV_ID = 'revision-name-div-id';
export const REVISION_PUBLISH_TIME_SPAN_ID = 'revision-publish-time-span-id';

const MenuItemFormatter = (name: string): React.ComponentClass<DisplayValueFormatterProps> => {
  return class Formatter extends React.Component<DisplayValueFormatterProps> {
    public render(): JSX.Element {
      return <div className={'tb-popup-menu-item'} id={REVISION_ACTION_POP_UP_MENU_ITEM_ID}>{name}</div>;
    }
  };
};

function getMenuActions(contentType: IContentType): RevisionHistoryActionButton<IContentRevision>[]{
  let menuActions: RevisionHistoryActionButton<IContentRevision>[];
  switch (contentType.name) {
    case WorkbookContentType.name:
      menuActions = RevisionActionWorkbook.getWorkbookRevisionActionButtons();
      break;
    case DatasourceContentType.name:
      menuActions = RevisionActionDatasource.getDatasourceRevisionActionButtons();
      break;
    case FlowContentType.name:
      menuActions = RevisionActionFlow.getFlowRevisionActionButtons();
      break;
    default:
      throw new Error(Messages.revisionHistoryUnsupportedContentType());
  }
  return menuActions;
}

export type RevisionHistoryActionCellFormatterProps = BodyCell.Props & Pick<RevisionHistoryDialogBodyStateAndProps,
                                                                              'updateDialogState' | 'contentType'>;

export function RevisionHistoryActionCellFormatter(props: RevisionHistoryActionCellFormatterProps) {
    const menuActions = getMenuActions(props.contentType);
    const menuItems = menuActions.map((action, index) => ({
        disabled: false,
        displayFormatterBoundWithData: MenuItemFormatter(TypeUnsafeTranslation.translate(action.translate)),
        value: `${index}`,
    }));
    const revision = props.row.data as IContentRevision;
    const divStyle = {display: revision.deleted || revision.current ? 'none' : 'initial'};
    const iconStyle = {verticalAlign: 'middle', align: 'center'};
    const flyoutMenuStyles = {
        backgroundColor: TabStyles.Colors.White,
        borderWidth: TabStyles.Sizing.BorderWidth,
        borderStyle: 'solid',
        width: '144px',
        shadow: TabStyles.Shadow.Standard,
    };

    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const buttonRef = useRef<HTMLButtonElement>();
    const handleMenuSelect = React.useCallback(val => {
        const context: RevisionHistoryActionContext<IContentRevision> = {
                          selectedItem: revision,
                          updateState: () => props.updateDialogState(
                              (prevState: RevisionHistoryDialogBodyState) => {
                                return {...prevState, deletedItem: prevState.selectedItem, selectedItem: null} as RevisionHistoryDialogBodyState;
                              })
                          };
        menuActions[val as number].execute(context);
        setIsOpen(false);
    }, [revision]);
    const handleMenuClose = React.useCallback(() => setIsOpen(false), []);
    const handleClick = () => setIsOpen(!isOpen);

    return <div style={divStyle}>
        <ButtonWidget testId={REVISION_ACTION_CELL_BUTTON_WIDGET_ID} buttonType={ButtonType.IconOutline} onClick={handleClick}
                      aria-controls={'test'} style={{...iconStyle}}
                      buttonRef={buttonRef}>
            <Icons.actionIcon style={iconStyle}/>
        </ButtonWidget>
        {isOpen &&
        <GenericButtonControlledFlyoutListMenu testId={REVISION_ACTION_CELL_FLYOUT_MENU_ID}
                                               anchorElement={buttonRef.current}
                                               menuItems={menuItems}
                                               children={''}
                                               onMenuItemSelected={handleMenuSelect}
                                               closeMe={handleMenuClose}
                                               onComponentDidMount={_.noop}
                                               menuStyle={flyoutMenuStyles}
        />
        }
    </div>;
}

export interface RevisionNameFormatterProps {
  bodyCellProps: BodyCell.Props;
  contentType: IContentType;
  onDeletedRevision: (rowKey: string) => void;
  onCurrentRevision: (rowKey: string) => void;
}

export function RevisionNameFormatter(props: RevisionNameFormatterProps) {
  const revision = props.bodyCellProps.row.data as IContentRevision;
  let cellStyle = getCellStyle(revision);

  const revisionName = Messages.revisionHistoryRevisionNumberLabel({REVISION_NUMBER: props.bodyCellProps.value});
  let revisionNameElement: JSX.Element = <span id={REVISION_NAME_SPAN_ID}>{revisionName}</span>;
  if (props.contentType.name === WorkbookContentType.name && !revision.deleted && !revision.current) {
    // the name element links to Preview
    const path = new SiteUrls({
      siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName(),
      queryString: window.location.search,
    }).workbookRevisionPreviewUrl(revision.revisionPath);
    const anchorProps = {id: REVISION_NAME_ANCHOR_ID, href: path, target: '_blank'};
    revisionNameElement = <a {...anchorProps}>{revisionName}</a>;
  }

  let revisionStateElement: React.ReactElement<React.HTMLAttributes<HTMLElement>> = null;
  if (revision.deleted) {
    revisionStateElement = <span>{' (' + Messages.revisionDeletedLabel() + ')'}</span>;
    props.onDeletedRevision(props.bodyCellProps.row.key);
  } else if (revision.current) {
    revisionStateElement = <span style={{color: CommonStyles.Colors.DisabledTextColor}}>{' (' + Messages.revisionCurrentLabel() + ')'}</span>;
    props.onCurrentRevision(props.bodyCellProps.row.key);
  }

  return revisionStateElement
    ? <div style={cellStyle} id={REVISION_NAME_DIV_ID}>{revisionNameElement}{revisionStateElement}</div>
    : revisionNameElement;
}

export function PublisherNameFormatter(props: BodyCell.Props) {
  const revision = props.row.data as IContentRevision;
  let cellStyle = getCellStyle(revision);
  return props.row.data.publisher
    ? <span style={cellStyle}>{props.row.data.publisher}</span>
    : <span className={'tb-hidden-user'} style={cellStyle}>{Messages.hiddenUser()}</span>;
}

function getCellStyle(revision: IContentRevision) {
  let cellStyle: React.CSSProperties = {};
  if (revision.deleted) {
    cellStyle = {color: CommonStyles.Colors.DisabledTextColor};
  }
  return cellStyle;
}

export function PublishTimeFormatter(props: BodyCell.Props) {
  const dateTime = props.row.data['publishTime'];
  const text = dateTime ? L10n.localizedDateTime(dateTime) : '';
  const revision = props.row.data as IContentRevision;
  let cellStyle = getCellStyle(revision);
  return <span data-tb-test-id={'publish-time-span-id'} id={REVISION_PUBLISH_TIME_SPAN_ID} style={cellStyle}>{text}</span>;
}